// src/components/sections/Projects.js
import React, { useEffect, useState, useContext } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { db } from '../../firebase/config';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaChevronRight } from 'react-icons/fa';
import ThemeBackground from '../theme/ThemeBackground';
import { ThemeContext } from '../../contexts/ThemeContext';

const StyledProjectsSection = styled(ThemeBackground)`
  padding: 7rem 0;
  position: relative;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  padding-bottom: 1rem;
  font-size: 2.5rem;
  color: var(--secondary-color);
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, var(--secondary-color), var(--primary-color));
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.25rem;
  max-width: 700px;
  margin: 0 auto;
  color: var(--text-color);
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
`;

const ProjectCard = styled(motion.div)`
  background-color: var(--card-bg);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.4s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  }
`;

const ProjectImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 16 / 8; /* Maintains consistent ratio across all project cards */
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProjectImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: ${props => (props.$isLpWealth || props.$isEudo) ? 'cover' : 'contain'}; /* LP Wealth and Eudo cover area, others show full image */
  object-position: ${props => (props.$isLpWealth || props.$isEudo) ? 'top' : 'center'}; /* LP Wealth and Eudo align to top, others centered */
  transition: transform 0.5s ease;

  ${ProjectCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProjectOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 100%);
  opacity: 0.7;
  transition: opacity 0.5s ease;
  
  ${ProjectCard}:hover & {
    opacity: 0.9;
  }
`;

const ProjectContent = styled.div`
  padding: 1.75rem;
`;

const ProjectCategory = styled.span`
  display: inline-block;
  padding: 0.35rem 0.8rem;
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--primary-color);
  border-radius: 30px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
`;

const ProjectTitle = styled.h3`
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--secondary-color);
  transition: color 0.3s ease;
  
  ${ProjectCard}:hover & {
    color: var(--primary-color);
  }
`;

const ProjectSummary = styled.p`
  margin-bottom: 1.25rem;
  color: var(--text-color);
  line-height: 1.6;
  font-size: 1rem;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 1.5rem;
`;

const ProjectTag = styled.span`
  padding: 0.35rem 0.75rem;
  background-color: ${props => props.isLight ? '#f0f4f8' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--secondary-color);
  transition: all 0.3s ease;
  
  ${ProjectCard}:hover & {
    background-color: rgba(59, 130, 246, 0.1);
    color: var(--primary-color);
  }
`;

const ProjectDetailsLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.95rem;
  margin-top: 0.5rem;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }

  &:hover {
    color: var(--accent-color);

    svg {
      transform: translateX(3px);
    }
  }
`;

const DisabledProjectLink = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-color);
  font-weight: 600;
  font-size: 0.95rem;
  margin-top: 0.5rem;
  opacity: 0.5;
  font-style: italic;
`;

const DecorativeShape = styled.div`
  position: absolute;
  opacity: 0.03;
  z-index: 0;
  
  &.shape-1 {
    top: 10%;
    right: -100px;
    width: 300px;
    height: 300px;
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    background-color: var(--primary-color);
  }
  
  &.shape-2 {
    bottom: 5%;
    left: -150px;
    width: 350px;
    height: 350px;
    border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%;
    background-color: var(--accent-color);
  }
`;

// Loading component
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top-color: var(--primary-color);
    animation: spin 1s ease-in-out infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isLight } = useContext(ThemeContext);
  
  // Updated project data with correct image paths
  const customProjects = [
    {
      id: 'eudo',
      title: 'HR Analytics & Experimentation Platform',
      summary: 'Research-driven platform enabling HR to prove ROI through controlled experiments and difference-in-differences analysis.',
      description: `HR departments invest millions in initiatives without knowing what actually works. Eudo solves HR's measurement problem by bringing scientific rigor to people analytics through controlled experiments, academically-validated diagnostics, and difference-in-differences methodology to isolate causal impact. Designed for Fortune 500 HR leaders who need to defend budgets to CFOs with data-driven ROI proof.`,
      technologies: ['React', 'TypeScript', 'Next.js', 'Node.js', 'Firebase', 'BigQuery', 'Figma', 'UX Research', 'User Testing', 'Data Visualization', 'Information Architecture', 'REST APIs'],
      imageUrl: '/assets/images/eudo/eudo7.png',
      projectUrl: 'https://eudo.co',
      loginRequired: true,
      features: [
        'User research with 5 Fortune 500 HR professionals',
        '8-phase experiment lifecycle management system',
        'Psychometrically-validated diagnostic (9 elements, α > 0.87)',
        'Difference-in-differences causal impact analysis',
        'Reduced experiment setup time from 10+ hours to <2 hours',
        'Intelligent filtering across 10 demographic dimensions',
        'Academic validation with Michigan State University',
        'Financial impact calculator mapping metrics to ROI'
      ]
    },
    {
      id: 'efilmhub',
      title: 'Film Industry Platform',
      summary: 'Social platform for film industry professionals combining networking, talent discovery, and collaboration.',
      description: `**The Challenge:** Film professionals in Africa lack centralized platforms for networking, hiring talent, and accessing equipment, forcing them to rely on fragmented social media groups and informal connections that limit industry growth and collaboration opportunities.

**My Role:** Lead UX Designer responsible for user research, persona development, user flow design, wireframing, prototyping, and responsive design system creation.

**Research & Discovery:**
Conducted contextual inquiry with 15 film professionals across different roles (directors, cinematographers, producers, actors) in African film markets to understand workflows, pain points, and networking behaviors. Key insights:
• Users needed verified professional profiles to establish credibility
• Talent discovery required robust filtering by skill, location, and availability
• Equipment sharing was informal and risky without structured marketplace
• Mobile-first design was critical due to primary device usage patterns

Developed 6 detailed user personas representing diverse film industry roles, each with unique needs and technological comfort levels.

**Design Process:**
• Mapped comprehensive user journeys for key scenarios: talent search, job posting, equipment rental, and professional networking
• Created information architecture combining IMDb-style profile presentation with Backstage hiring functionality
• Designed low-fidelity wireframes for 20+ core screens, tested with 5 users for initial feedback
• Developed high-fidelity prototypes in Figma featuring intuitive navigation and mobile-optimized layouts
• Established scalable design system with reusable components for consistent experience

**Key Design Solutions:**
• Portfolio-centric profiles with media galleries showcasing previous work
• Advanced filtering system for talent search by role, experience, location, and availability
• Streamlined job posting flow reducing time-to-post from 10 minutes to under 2 minutes
• Equipment marketplace with verification and rating systems for safe transactions
• Real-time messaging and collaboration tools integrated throughout platform

**User Testing & Iteration:**
Conducted two rounds of usability testing with 10 film professionals, revealing:
• Users preferred visual portfolio browsing over text-heavy profiles
• Search filters needed to be more prominent and accessible
• Mobile navigation required simplification for one-handed use

Iterated designs based on testing insights, enhancing visual hierarchy and mobile navigation patterns.

**Impact:**
Successfully created a unified platform addressing the fragmented nature of film industry networking in African markets, with initial user testing showing 85% task completion rate for core workflows.`,
      technologies: ['React', 'TypeScript', 'Next.js', 'Node.js', 'GCP', 'Cloud Storage', 'Real-time Database', 'Figma', 'UX Research', 'Wireframing', 'User Personas', 'REST APIs'],
      imageUrl: '/assets/images/efilmhub.png',
      projectUrl: 'https://efilmhub.com',
      loginRequired: true,
      features: [
        'Contextual inquiry with 15 film industry professionals',
        'User persona development for 6 diverse film roles',
        'Comprehensive user journey mapping',
        'Low and high-fidelity wireframing and prototyping',
        'Mobile-first responsive design system',
        'Two rounds of usability testing with iterative improvements',
        'Information architecture combining profile, hiring, and marketplace features',
        '85% task completion rate in user testing'
      ]
    },
  ];

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        // Try to get projects from Firebase
        const projectsCollection = collection(db, 'projects');
        const projectsSnapshot = await getDocs(projectsCollection);
        const projectsList = projectsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        if (projectsList.length > 0) {
          setProjects(projectsList);
        } else {
          // Use the custom projects if Firebase is empty or fails
          setProjects(customProjects);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        // Use the custom projects if Firebase fails
        setProjects(customProjects);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  return (
    <>
      <StyledProjectsSection id="projects">
        <DecorativeShape className="shape-1" />
        <DecorativeShape className="shape-2" />
        
        <div className="container">
          <SectionHeader>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true, amount: 0.2 }}
            >
              <SectionTitle>Featured Projects</SectionTitle>
              <SectionSubtitle>
                A selection of my most recent and impactful work
              </SectionSubtitle>
            </motion.div>
          </SectionHeader>
          
          {loading ? (
            <LoadingSpinner>
              <div className="spinner"></div>
            </LoadingSpinner>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
            >
              <ProjectsGrid>
                {projects.map((project) => (
                  <motion.div key={project.id} variants={itemVariants}>
                    <ProjectCard>
                      <ProjectImageContainer>
                        <ProjectImage
                          src={project.imageUrl}
                          alt={project.title}
                          $isLpWealth={project.id === 'lpwealthgroup'}
                          $isEudo={project.id === 'eudo'}
                          onError={(e) => {
                            // Fixed fallback image paths
                            if (project.id === 'eudo') {
                              e.target.src = "/assets/images/eudo/eudo7.png";
                            } else if (project.id === 'efilmhub') {
                              e.target.src = "/assets/images/efilmhub.png";
                            } else if (project.id === 'lpwealthgroup') {
                              e.target.src = "/assets/images/lpwealthgroup/lpwealthgroup.png";
                            } else {
                              // If all else fails, use an icon from a CDN
                              e.target.src = "https://cdn-icons-png.flaticon.com/512/1356/1356479.png";
                            }
                          }}
                        />
                        <ProjectOverlay />
                      </ProjectImageContainer>
                      
                      <ProjectContent>
                        <ProjectTitle>{project.title}</ProjectTitle>
                        <ProjectSummary>{project.summary}</ProjectSummary>
                        <ProjectTags>
                          {project.technologies && project.technologies.slice(0, 4).map((tech, techIndex) => (
                            <ProjectTag key={techIndex} isLight={isLight}>{tech}</ProjectTag>
                          ))}
                        </ProjectTags>
                        {(project.id === 'efilmhub' || project.id === 'lpwealthgroup' || project.id === 'eudo') ? (
                          <ProjectDetailsLink to={`/projects/${project.id}`}>
                            View Case Study <FaChevronRight />
                          </ProjectDetailsLink>
                        ) : (
                          <DisabledProjectLink>
                            → Case Study Coming Soon
                          </DisabledProjectLink>
                        )}
                      </ProjectContent>
                    </ProjectCard>
                  </motion.div>
                ))}
              </ProjectsGrid>
            </motion.div>
          )}
        </div>
      </StyledProjectsSection>
    </>
  );
};

export default Projects;